import SEO from '@/components/SEO';
import { Link } from 'react-router-dom';
import { Shield, Check, X, ArrowRight, Phone, PoundSterling, Wrench, Zap, AlertTriangle } from 'lucide-react';

const includes = [
  { title: 'Parts & Labour', desc: 'All replacement parts and technician labour costs covered for the full term.' },
  { title: 'Power Supply Repairs', desc: 'PSU faults, capacitor failure, voltage regulator issues — all covered.' },
  { title: 'Overheating & Thermal', desc: 'Fan replacement, thermal paste renewal, heatsink cleaning, sensor faults.' },
  { title: 'Display & Optics', desc: 'LCD polarizer replacement, colour wheel repair, optical block cleaning, DMD issues.' },
  { title: 'Mainboard & HDMI', desc: 'Board-level micro-soldering repairs, HDMI port replacement, input board faults.' },
  { title: 'Collection & Return', desc: 'Free collection and return delivery for warranty claims — no extra cost.' },
  { title: 'Priority Turnaround', desc: 'Warranty claims jump the queue. 24-48 hour assessment, 2-4 day repair.' },
  { title: 'Annual Health Check', desc: 'One complimentary annual inspection including filter clean, fan test and firmware update.' },
];

const excludes = [
  'Replacement lamps (these are consumables with a finite lifespan)',
  'Physical damage caused by drops, spills, or mishandling',
  'Damage from power surges without a surge protector',
  'Unauthorized repairs or tampering with internal components',
  'Consumables such as filters, dust covers and remote controls',
];

const faqs = [
  { q: 'How much does the extended warranty cost?', a: 'Our extended warranty costs just 45p per day — £164.25 per year. That is less than a cup of coffee per week for complete peace of mind.' },
  { q: 'Why are lamps not covered?', a: 'Projector lamps are consumable items with a rated lifespan (typically 2,000-6,000 hours). Like printer ink or car tyres, they wear out through normal use and must be replaced periodically. We do offer genuine OEM lamp supply and fitting at competitive rates.' },
  { q: 'When does the warranty period start?', a: 'The extended warranty begins the day your repair is completed and your projector is returned to you. It runs for 12 months from that date.' },
  { q: 'Can I buy extended warranty without a repair?', a: 'Yes. If your projector is in good working order, we can perform a health check (£49, deducted from warranty price if purchased) and then activate your extended warranty.' },
  { q: 'How do I make a warranty claim?', a: 'Simply call us on 0333 006 4326 or email wellselectronicsltd@gmail.com with your warranty reference number. We will arrange free collection within 24 hours.' },
  { q: 'Is the warranty transferable if I sell my projector?', a: 'Yes. The warranty is tied to the projector serial number, not the owner. Provide the new owner with your warranty certificate and they are fully covered.' },
  { q: 'What if the same fault happens again?', a: 'We fix it free of charge, including all parts and labour. If the fault is unrepairable, we offer a replacement unit at trade cost or a pro-rata refund.' },
  { q: 'Do you cover commercial and installation projectors?', a: 'Yes. Our extended warranty covers all projector types including large-venue, commercial and installation models. Commercial units may have different terms — contact us for a bespoke quote.' },
];

export default function ExtendedWarrantyPage() {
  return (
    <>
      <SEO
        title="Extended Warranty | 45p/Day Projector Protection | Projector Repair London"
        description="Protect your projector for just 45p per day. 12-month extended warranty covers parts, labour, power supply, overheating, display and mainboard repairs. Free collection."
        canonical="https://projectorrepairlondon.co.uk/extended-warranty"
      />

      {/* Hero — Blue with animated warranty badge */}
      <section className="bg-[#0057B7] pt-32 pb-16 md:pt-40 md:pb-24 overflow-hidden relative">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-32 h-32 border-4 border-[#FFDD00] rounded-full" />
          <div className="absolute bottom-10 right-10 w-48 h-48 border-4 border-[#FFDD00] rounded-full" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 border-2 border-[#FFDD00] rounded-full" />
        </div>
        <div className="container-custom relative z-10">
          <nav className="mb-6"><ol className="flex items-center gap-2 text-sm">
            <li><Link to="/" className="text-white/50 hover:text-white transition-colors">Home</Link></li>
            <li className="text-white/30">/</li>
            <li className="text-white font-medium">Extended Warranty</li>
          </ol></nav>

          <div className="flex flex-wrap items-center gap-3 mb-6">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#FFDD00] text-[#0057B7] rounded-full text-sm font-bold">
              <Shield className="w-4 h-4" /> 12 MONTH PROTECTION
            </span>
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 text-[#FFDD00] rounded-full text-sm font-semibold border border-[#FFDD00]/30">
              <PoundSterling className="w-4 h-4" /> 45p PER DAY
            </span>
          </div>

          <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-extrabold text-white max-w-3xl leading-tight">
            Projector Extended Warranty — <span className="text-[#FFDD00]">Complete Peace of Mind</span>
          </h1>
          <p className="mt-5 text-white/70 max-w-xl leading-relaxed text-lg">
            Protect your investment for just 45p per day. Our 12-month extended warranty covers parts, labour, power supply, overheating, display, and mainboard repairs — with free collection and priority turnaround.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <Link to="/repair-enquiry" className="inline-flex items-center gap-2 px-8 py-4 bg-[#FFDD00] text-[#0057B7] rounded-lg font-bold hover:bg-[#C9A800] transition-all text-lg">
              Get Protected <ArrowRight className="w-5 h-5" />
            </Link>
            <a href="tel:03330064326" className="inline-flex items-center gap-2 px-8 py-4 bg-white/10 text-white rounded-lg font-medium hover:bg-white/20 transition-all text-lg border border-white/20">
              <Phone className="w-5 h-5" /> 0333 006 4326
            </a>
          </div>

          {/* Price cards */}
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/10 text-center">
              <p className="text-3xl font-bold text-[#FFDD00]">45p</p>
              <p className="text-sm text-white/60 mt-1">per day</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/10 text-center">
              <p className="text-3xl font-bold text-[#FFDD00]">£164.25</p>
              <p className="text-sm text-white/60 mt-1">per year</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/10 text-center">
              <p className="text-3xl font-bold text-[#FFDD00]">12</p>
              <p className="text-sm text-white/60 mt-1">months cover</p>
            </div>
          </div>
        </div>
      </section>

      {/* What is Covered — White */}
      <section className="bg-white py-12 lg:py-16">
        <div className="container-custom">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="font-label text-[#0057B7] bg-[#0057B7]/10 px-3 py-1 rounded-full text-xs">What is Covered</span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#0057B7] mt-3">8 Reasons to Choose Our Extended Warranty</h2>
            <p className="mt-3 text-[#0057B7]/70 text-sm">Comprehensive protection for your projector. If it breaks, we fix it — simple as that.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 reveal-up">
            {includes.map((item, i) => (
              <div key={i} className="bg-[#E8F0FE] rounded-xl p-6 border border-[#0057B7]/5 hover:border-[#FFDD00]/40 transition-all group">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#0057B7]/10 text-[#0057B7] mb-4 group-hover:bg-[#FFDD00]/20 group-hover:text-[#0057B7] transition-colors">
                  <Check className="w-5 h-5" />
                </div>
                <h3 className="font-heading font-semibold text-[#0057B7] mb-2">{item.title}</h3>
                <p className="text-sm text-[#0057B7]/60 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lamp Exclusion Banner — Yellow */}
      <section className="bg-[#FFDD00] py-12 lg:py-16">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-start gap-4 bg-[#0057B7]/10 rounded-2xl p-6 md:p-8 border border-[#0057B7]/20">
              <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-[#0057B7] shrink-0">
                <AlertTriangle className="w-7 h-7 text-[#FFDD00]" />
              </div>
              <div>
                <h3 className="font-heading text-xl font-bold text-[#0057B7] mb-3">What is NOT Covered — Replacement Lamps</h3>
                <p className="text-[#0057B7]/80 leading-relaxed mb-4">
                  Projector lamps are <strong>consumable items</strong> with a finite rated lifespan — typically 2,000 to 6,000 hours depending on the model and usage mode. Like printer ink, car tyres, or camera batteries, they wear out through normal use and must be replaced as part of regular maintenance.
                </p>
                <p className="text-[#0057B7]/80 leading-relaxed mb-4">
                  Our extended warranty covers the <strong>projector mechanism and electronics</strong> — not consumable components. However, as a warranty holder, you receive <strong>20% off</strong> all genuine OEM lamp replacements for the duration of your cover.
                </p>
                <div className="flex flex-wrap gap-2 mt-4">
                  {excludes.map((item, i) => (
                    <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#0057B7]/10 text-[#0057B7] text-xs font-medium rounded-lg">
                      <X className="w-3 h-3" />{item}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works — White */}
      <section className="bg-white py-12 lg:py-16">
        <div className="container-custom">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="font-label text-[#0057B7] bg-[#0057B7]/10 px-3 py-1 rounded-full text-xs">How It Works</span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#0057B7] mt-3">3 Simple Steps to Activate</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {[
              { step: '01', title: 'Repair or Health Check', desc: 'Have your projector repaired by us, or book a health check if it is in good working order. The warranty activates from the completion date.', icon: Wrench },
              { step: '02', title: 'Purchase Warranty', desc: 'Pay £164.25 for 12 months cover. We will issue a warranty certificate with your unique reference and projector serial number.', icon: Shield },
              { step: '03', title: 'Full Protection', desc: 'If anything goes wrong, call us. We collect free of charge, repair under warranty, and return your projector. Priority turnaround guaranteed.', icon: Zap },
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#0057B7] text-[#FFDD00] font-heading font-bold text-xl mx-auto mb-4">
                  <item.icon className="w-7 h-7" />
                </div>
                <h3 className="font-heading font-semibold text-lg text-[#0057B7] mb-2">{item.title}</h3>
                <p className="text-sm text-[#0057B7]/60 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Price Comparison — Light Blue */}
      <section className="bg-[#E8F0FE] py-12 lg:py-16">
        <div className="container-custom">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="font-label text-[#0057B7] bg-[#0057B7]/10 px-3 py-1 rounded-full text-xs">Value</span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#0057B7] mt-3">Warranty vs Pay-As-You-Go</h2>
          </div>
          <div className="max-w-2xl mx-auto bg-white rounded-xl overflow-hidden border border-[#0057B7]/5 shadow-lg">
            <table className="w-full">
              <thead><tr className="bg-[#0057B7]">
                <th className="px-6 py-4 text-left text-xs font-label text-[#0057B7]">Scenario</th>
                <th className="px-6 py-4 text-right text-xs font-label text-white/60">Without Warranty</th>
                <th className="px-6 py-4 text-right text-xs font-label text-[#0057B7]">With Warranty</th>
              </tr></thead>
              <tbody className="divide-y divide-[#E8F0FE]">
                {[
                  { scenario: 'Annual cost', without: '£0', withw: '£164.25' },
                  { scenario: 'One power supply repair', without: '£180-£250', withw: '£0 (covered)' },
                  { scenario: 'Overheating / thermal fix', without: '£120-£180', withw: '£0 (covered)' },
                  { scenario: 'Display / polarizer repair', without: '£150-£220', withw: '£0 (covered)' },
                  { scenario: 'Mainboard micro-soldering', without: '£200-£350', withw: '£0 (covered)' },
                  { scenario: 'Collection & return', without: '£0-£30', withw: '£0 (covered)' },
                ].map((row, i) => (
                  <tr key={i} className="hover:bg-[#E8F0FE]/50 transition-colors">
                    <td className="px-6 py-4 font-medium text-[#0057B7] text-sm">{row.scenario}</td>
                    <td className="px-6 py-4 text-right text-sm text-[#0057B7]/50">{row.without}</td>
                    <td className="px-6 py-4 text-right text-sm font-semibold text-[#0057B7]">{row.withw}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="px-6 py-4 bg-[#FFDD00]/10 border-t border-[#FFDD00]/20">
              <p className="text-sm text-[#0057B7] text-center font-medium">
                <strong>One repair</strong> typically costs more than the entire annual warranty. Break even after your first claim.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ — White */}
      <section className="bg-white py-12 lg:py-16">
        <div className="container-custom">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="font-label text-[#0057B7] bg-[#0057B7]/10 px-3 py-1 rounded-full text-xs">FAQ</span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#0057B7] mt-3">Extended Warranty Questions</h2>
          </div>
          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-[#E8F0FE] rounded-xl p-5 border border-[#0057B7]/5">
                <h3 className="font-heading font-semibold text-[#0057B7] mb-2 flex items-start gap-2">
                  <span className="w-6 h-6 flex items-center justify-center rounded-full bg-[#0057B7] text-[#FFDD00] text-xs font-bold shrink-0 mt-0.5">{i + 1}</span>
                  {faq.q}
                </h3>
                <p className="text-sm text-[#0057B7]/70 leading-relaxed ml-8">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA — Blue */}
      <section className="bg-[#0057B7] py-16">
        <div className="container-custom text-center">
          <Shield className="w-12 h-12 text-[#FFDD00] mx-auto mb-4" />
          <h2 className="font-heading text-3xl font-bold text-white">Protect Your Projector Today</h2>
          <p className="mt-4 text-white/70 max-w-lg mx-auto">45p per day. £164.25 per year. Complete protection for parts, labour and electronics. Free collection. Priority turnaround.</p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <Link to="/repair-enquiry" className="inline-flex items-center gap-2 px-8 py-4 bg-[#FFDD00] text-[#0057B7] rounded-lg font-bold hover:bg-[#C9A800] transition-all text-lg">
              Get Extended Warranty <ArrowRight className="w-5 h-5" />
            </Link>
            <a href="tel:03330064326" className="inline-flex items-center gap-2 px-8 py-4 bg-white/10 text-white rounded-lg font-medium hover:bg-white/20 transition-all text-lg border border-white/20">
              <Phone className="w-5 h-5" /> 0333 006 4326
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
